import type { BenchmarkSetup, BenchmarkTechnicalInput, BenchmarkFlowMapping } from '../pages/Benchmarking/types';
import type {
  BenchmarkCreateRequestDto,
  BenchmarkDisplayRequestDto,
  BenchmarkFlowMappingRequestDto,
  BenchmarkMappingRequestDto,
  BenchmarkSetupRequestDto,
  BenchmarkSetupResponseDto,
} from './benchmark-setups.types';

export const BENCHMARK_DIMENSIONS = ['age_group', 'gender', 'tenure', 'manager'];

export function toCreateRequest(input: { key: string; name: string }): BenchmarkCreateRequestDto {
  return { key: input.key.trim(), name: input.name.trim() };
}

export function toFlowMappingRequest(input: {
  flowId: string;
  measureMappings: Record<string, string>;
}): BenchmarkFlowMappingRequestDto {
  return { flow_id: input.flowId, measure_mappings: input.measureMappings };
}

export function toMappingRequest(input: {
  nodeId: string;
  flows: { flowId: string; measureMappings: Record<string, string> }[];
}): BenchmarkMappingRequestDto {
  return { node_id: input.nodeId, flows: input.flows.map(toFlowMappingRequest) };
}

export function toDisplayRequest(input: {
  name: string;
  measures: { id: string; name: string }[];
  levels: { id: string; name: string }[];
}): BenchmarkDisplayRequestDto {
  return { name: input.name.trim(), measures: input.measures, benchmark_levels: input.levels };
}

export function toTechnicalRequest({ basic, measures, levels }: BenchmarkTechnicalInput): BenchmarkSetupRequestDto {
  return {
    provider: { key: 'ipsos', name: 'Ipsos' },
    metric_key: basic.metric,
    timezone: basic.timeZone,
    dimensions: [...basic.dimensions].sort((a, b) => {
      const order = (code: string) => {
        const index = BENCHMARK_DIMENSIONS.indexOf(code);
        return index < 0 ? BENCHMARK_DIMENSIONS.length : index;
      };
      return order(a) - order(b);
    }),
    internal_period_granularity: basic.internalPeriod,
    measures: measures.map((measure, index) => ({
      key: measure.internalKey.trim(),
      name: measure.displayName.trim(),
      kind: 'question',
      ex_area: measure.exArea.trim(),
      provider_measure_key: measure.providerKey.trim(),
      provider_wording: measure.authoritativeWording.trim(),
      display_order: index,
    })),
    benchmark_levels: levels.map((level) => ({ key: level.levelKey.trim(), name: level.displayName.trim() })),
  };
}

export function fromSetupResponse(dto: BenchmarkSetupResponseDto): BenchmarkSetup {
  const technical = dto.technical_configuration;
  const definitions = dto.customer_success_configurations;
  const flows = new Map<string, BenchmarkFlowMapping>();
  const nodeIds = new Set(definitions.flatMap((definition) => definition.node_ids));
  const measureKeys = definitions.map((definition) => definition.measure_key);
  const mappingAmbiguous =
    new Set(measureKeys).size !== measureKeys.length ||
    (definitions.length > 0 && nodeIds.size !== 1) ||
    definitions.some(
      (definition) =>
        definition.node_ids.length !== 1 ||
        new Set(definition.question_sources.map((source) => source.flow_id)).size !==
          definition.question_sources.length,
    );
  for (const definition of definitions) {
    for (const source of definition.question_sources) {
      const flow = flows.get(source.flow_id) || {
        id: flows.size,
        survey: source.flow_id,
        questions: {},
        persisted: true,
      };
      flow.questions[definition.measure_key] = source.question_id;
      flows.set(source.flow_id, flow);
    }
  }
  return {
    id: dto.id,
    updatedAt: dto.updated_at,
    accountId: dto.account_id,
    status: dto.status,
    basic: {
      name: dto.name,
      key: dto.key,
      metric: technical?.metric_key || 'top_n_csat_v1',
      timeZone: technical?.timezone || '',
      dimensions: technical?.dimensions || [],
      internalPeriod: technical?.internal_period_granularity || 'calendar_month',
    },
    measures:
      technical?.measures.map((measure, index) => ({
        id: index,
        serverId: measure.id,
        internalKey: measure.key,
        displayName: measure.name,
        exArea: measure.ex_area,
        providerKey: measure.provider_measure_key,
        authoritativeWording: measure.provider_wording || '',
      })) || [],
    levels:
      technical?.benchmark_levels.map((level, index) => ({
        id: index,
        serverId: level.id,
        levelKey: level.key,
        displayName: level.name,
      })) || [],
    flows: [...flows.values()],
    nodeId: nodeIds.size === 1 ? [...nodeIds][0] : '',
    aggregateDefinitionIds: definitions.map((definition) => definition.id),
    mappingConfigured: definitions.length > 0,
    mappingAmbiguous,
    technicalReady: dto.readiness.technical.status === 'ready',
    mappingReady: dto.readiness.customer_success.status === 'ready',
  };
}
